/**
 * A conexão do usuário pede economia? (`navigator.connection`, Chrome/Edge/
 * Android; Safari e Firefox não informam e valem como boa conexão.)
 *
 * Econômica: "Economia de dados" ligada, conexão efetiva 2G/3G (o navegador
 * mede ida e volta e banda), banda abaixo de 2 Mbps, ou ida e volta de 150 ms
 * ou mais com menos de 3 Mbps. O "3G rápido" (1,6 Mbps, 150 ms) o Chrome
 * informa como '4g' — só a banda e a ida e volta o pegam (medido: sem isso, 44 s
 * até a vista nítida no celular). Aí o detalhe fica em 3600 px, a base de perto
 * em 450, só os quadros da janela são baixados adiante e não há pré-busca em
 * volta da vista. Com boa conexão, nada muda: qualidade máxima, sem perguntar.
 *
 * Reage à troca de rede (evento `change`). `forcar` (debug/medição) passa por
 * cima do que o navegador diz.
 */
import { create } from 'zustand';

interface InfoConexao {
  saveData?: boolean;
  effectiveType?: string;
  /** Mbps estimados pelo navegador. */
  downlink?: number;
  /** Ida e volta estimada (ms). */
  rtt?: number;
}

interface ConexaoNavegador extends InfoConexao {
  addEventListener?: (tipo: 'change', fn: () => void) => void;
  removeEventListener?: (tipo: 'change', fn: () => void) => void;
}

/** Banda (Mbps) abaixo da qual a conexão é econômica. */
export const BANDA_MINIMA_MBPS = 2;
/** Ida e volta (ms) a partir da qual a conexão é econômica se a banda for menor que `BANDA_COM_DEMORA_MBPS`. */
export const DEMORA_MS = 150;
export const BANDA_COM_DEMORA_MBPS = 3;

/** Por que economizar (texto curto), ou null se a conexão é boa. Puro — testado. */
export function motivoDeEconomia(info: InfoConexao | null | undefined): string | null {
  if (!info) return null;
  if (info.saveData) return 'economia de dados';
  if (info.effectiveType && /^(slow-2g|2g|3g)$/.test(info.effectiveType)) return `conexão ${info.effectiveType}`;
  const banda = typeof info.downlink === 'number' && info.downlink > 0 ? info.downlink : null;
  if (banda !== null && banda < BANDA_MINIMA_MBPS) return `${banda} Mbps`;
  if (banda !== null && banda < BANDA_COM_DEMORA_MBPS && typeof info.rtt === 'number' && info.rtt >= DEMORA_MS) return `${banda} Mbps, ${info.rtt} ms`;
  return null;
}

/**
 * Conexão sabidamente rápida: o Chrome informa a banda até 10 Mbps (o teto) e a
 * ida e volta. Nela a base da abertura não precisa passar na frente dos blocos
 * (medido em fibra: esperar a base atrasava a vista nítida de 2,9 para 5,0 s).
 * Sem informação (Safari, Firefox), não é rápida. Puro — testado.
 */
export function conexaoRapida(info: InfoConexao | null | undefined): boolean {
  if (!info || info.saveData) return false;
  return typeof info.downlink === 'number' && info.downlink >= 10 && typeof info.rtt === 'number' && info.rtt < 100;
}

interface EstadoConexao {
  /** O que o navegador diz (null = boa ou desconhecida). */
  motivo: string | null;
  /** O navegador diz que a conexão é rápida (ver `conexaoRapida`). */
  rapida: boolean;
  /** Debug/medição: true/false passa por cima; null = segue o navegador. */
  forcar: boolean | null;
}

export const useConexaoStore = create<EstadoConexao>()(() => ({ motivo: null, rapida: false, forcar: null }));

/** Economizar dados agora? */
export function conexaoEconomica(s: EstadoConexao = useConexaoStore.getState()): boolean {
  return s.forcar ?? s.motivo !== null;
}

const conexaoDoNavegador = (): ConexaoNavegador | undefined =>
  typeof navigator !== 'undefined' ? (navigator as Navigator & { connection?: ConexaoNavegador }).connection : undefined;

/** Lê a conexão e passa a acompanhar as trocas. Retorna a função de desligar. */
export function acompanharConexao(): () => void {
  const c = conexaoDoNavegador();
  const ler = () => {
    const motivo = motivoDeEconomia(c);
    const rapida = conexaoRapida(c);
    const atual = useConexaoStore.getState();
    if (atual.motivo !== motivo || atual.rapida !== rapida) useConexaoStore.setState({ motivo, rapida });
  };
  ler();
  c?.addEventListener?.('change', ler);
  return () => c?.removeEventListener?.('change', ler);
}
